import { gql, useMutation, useQuery } from '@apollo/client'
import { Browser } from '@capacitor/browser'
import flatten from 'lodash/flatten'
import { useEffect, useMemo, useState } from 'react'

import {
  ArtistCategoryQuery,
  ArtistCategoryQueryVariables,
  ArtistSubscriptionPlanQuery,
  ArtistSubscriptionPlanQueryVariables,
  CreatePostInput,
  CreatePostMutation,
  CreatePostMutationVariables,
  MembershipInterval,
  PostBoxFragmentDoc,
  PostStatus,
  PostType,
} from '@/clients/utoniq-core/schema'
import { useTwitterConnection } from '@/components/pages/Integrations/viewModel'
import { config } from '@/config'
import { useAuth, useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { Camera, File } from '@/core/native'
import { getPlatform } from '@/core/native/app'
import { pickImages } from '@/core/native/camera'
import { getInputPostOptions, setInputPostOptions } from '@/core/native/storage'
import { useUserTmpUploader } from '@/core/resources/useTmpUploader'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation()
  const { showError } = useMessage()
  const { user, reloadUser } = useAuth()
  const [withTwitter, setWithTwitter] = useState<boolean>(false)
  const { router } = useRouter<{ resourceType?: PostType }>()
  const { resourceType } = router.query || {}
  const { getImage } = Camera

  const { data } = useQuery<
    ArtistSubscriptionPlanQuery,
    ArtistSubscriptionPlanQueryVariables
  >(QUERY)
  const { data: artistPostCategory } = useQuery<
    ArtistCategoryQuery,
    ArtistCategoryQueryVariables
  >(POST_CATEGORY_QUERY)
  const [createPost, { loading: loadingCreatePost }] = useMutation<
    CreatePostMutation,
    CreatePostMutationVariables
  >(CREATE_POST)
  const { upload, progress } = useUserTmpUploader(user?.uid || '')
  const [resourcesUploading, setResourcesUploading] = useState(false)
  const { connected: isConnectedTwitter, connectTwitter } =
    useTwitterConnection()

  useEffect(() => {
    setPostTwitterSetting()
    reloadWhenFinishBrowser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * localStorageからtwitter連携投稿に関する状態をセットする
   */
  const setPostTwitterSetting = () => {
    getInputPostOptions().then(value => {
      setWithTwitter(isConnectedTwitter && value.withTwitter)
    })
  }

  /**
   * アプリ内部ブラウザから戻ってきた時にユーザーの状態を更新する
   */
  const reloadWhenFinishBrowser = () => {
    Browser.addListener('browserFinished', () => {
      reloadUser().catch(showError)
    })
  }

  const getResource = async (
    resourceType: PostType | undefined,
    multiple?: boolean,
    limit?: number
  ) => {
    if (resourceType === PostType.Image) {
      const files = multiple
        ? await pickImages(config.upload.mimeTypes.image, limit)
        : [await getImage(config.upload.mimeTypes.image)]

      if (files.every(file => file.size > config.upload.limit['image'])) {
        throw new Error('uploadErrorMaximumSize')
      }
      return files
    }

    const platform = getPlatform()
    if (resourceType === PostType.Video) {
      const file = await File.pickFile(config.upload.mimeTypes.video)
      if (
        config.upload.mimeTypes.video.findIndex(o => o === file.mimeType) === -1
      ) {
        throw new Error('uploadErrorVideoOnly')
      }
      if (file.size > config.upload.limit['video'][platform]) {
        throw new Error('uploadErrorMaximumSize')
      }
      return [file]
    }

    if (resourceType === PostType.Sound) {
      const file = await File.pickFile(config.upload.mimeTypes.audio)
      if (
        config.upload.mimeTypes.audio.findIndex(o => o === file.mimeType) === -1
      ) {
        throw new Error('uploadErrorAudioOnly')
      }
      if (file.size > config.upload.limit['audio']) {
        throw new Error('uploadErrorMaximumSize')
      }
      return [file]
    }

    throw new Error('resourceType Error')
  }

  const plans = useMemo(() => {
    if (data?.artist?.subscription?.products?.length) {
      return flatten(
        data?.artist?.subscription?.products.map(product => {
          const monthPlan = product.plans.find(
            plan => plan.interval === MembershipInterval.Month
          )
          if (monthPlan) {
            return [{ ...monthPlan, title: product.title }]
          }
          return product.plans.map(plan => ({ ...plan, title: product.title }))
        })
      )
    }
    if (data?.artist?.subscription?.plans?.length) {
      return data.artist.subscription.plans
    }
    return []
  }, [data?.artist?.subscription?.plans, data?.artist?.subscription?.products])

  const categories = useMemo(() => {
    if (artistPostCategory?.artist?.postCategory?.categories.length) {
      return artistPostCategory?.artist?.postCategory?.categories
    }
    return []
  }, [artistPostCategory?.artist?.postCategory?.categories])

  return {
    state: {
      artistId: user?.uid ?? '',
      categories: categories,
      plans,
      withTwitter,
      resourceType: router.query.resourceType,
      isConnectedTwitter,
      resourcesUploading,
      loadingCreatePost,
      fileUploadInfo: {
        progress,
        async onDrop() {
          const files = await getResource(
            resourceType,
            resourceType === PostType.Image,
            resourceType === PostType.Image ? 10 : 1
          ).catch(error => {
            showError(new Error(t([error.message, 'interruptedUpload'])))
          })

          if (!files || !files?.length) {
            return
          }

          setResourcesUploading(true)

          return Promise.all(
            files.map(file =>
              upload(
                file,
                resourceType === PostType.Sound ||
                  resourceType === PostType.Video
              ).then(({ tmpPath, fileUrl }) => {
                const uuid = createUuid()
                return {
                  id: uuid,
                  resourceName: uuid,
                  imageUrl: fileUrl,
                  imageUploadPath: tmpPath,
                }
              })
            )
          ).finally(() => {
            setResourcesUploading(false)
          })
        },
      },
    },
    actions: {
      onSubmitPost: async (inputs: CreatePostInput) => {
        await createPost({
          variables: {
            inputs: {
              ...inputs,
              publishedAt: inputs.publishedAt ?? null,
              status: inputs.publishedAt
                ? PostStatus.Scheduled
                : PostStatus.Published,
            },
          },
        })
        await setInputPostOptions({ withTwitter: !!inputs.withTwitter })

        return
      },
      onConnectTwitter: connectTwitter,
    },
  }
})

const QUERY = gql`
  query artistSubscriptionPlan {
    artist {
      id
      subscription {
        plans {
          planId
          title
          interval
        }
        products {
          productId
          title
          plans {
            interval
            planId
            amount
          }
        }
      }
    }
  }
`

const POST_CATEGORY_QUERY = gql`
  query artistCategory {
    artist {
      id
      postCategory {
        categories {
          id
          name
        }
      }
    }
  }
`

const CREATE_POST = gql`
  mutation CreatePost($inputs: CreatePostInput!) {
    createPost(inputs: $inputs) {
      ...PostBox
    }
  }
  ${PostBoxFragmentDoc}
`
